import React, { useState, useEffect } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import {
  Calendar,
  Users,
  TrendingUp,
  Clock,
  Plus,
  Eye,
  ArrowRight,
  Activity,
} from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import AuctionService from "../../../services/auctionService";
import dashboardService, {
  FullDashboardApiResponse,
  DashboardUpcomingAuctionApi,
  DashboardRecentActivityApi,
} from "../../../services/dashboardService";
import apiAuctionService from "../../../services/apiAuctionService";
import { BaseAuction } from "../../../types/auction";
import "./DashboardHome.css";
import {
  Tag,
  UserPlus,
  PlusCircle,
  User,
  CheckCircle,
  BarChart,
  Search,
  Gavel,
} from "lucide-react";
import SuccessModal from "../../Common/SuccessModal";

/* ----------  format helpers  ---------- */
const fmtDate = (d: string | Date) => {
  const date = new Date(d);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(date.getDate())}-${pad(
    date.getMonth() + 1
  )}-${date.getFullYear()}`;
};

const fmtTime = (d: string | Date) =>
  new Date(d).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
/* -------------------------------------- */

const DashboardHome: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [myAuctions, setMyAuctions] = useState<BaseAuction[]>([]); // fallback/local
  const [participatedAuctions, setParticipatedAuctions] = useState<
    BaseAuction[]
  >([]); // fallback/local
  const [loading, setLoading] = useState(true);
  const [useRemote, setUseRemote] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState(
    "Here's what's happening with your auctions today."
  );
  const [remoteStats, setRemoteStats] = useState<
    FullDashboardApiResponse["dashboard"]["stats"] | null
  >(null);
  const [remoteUpcoming, setRemoteUpcoming] = useState<
    DashboardUpcomingAuctionApi[]
  >([]);
  const [remoteActivities, setRemoteActivities] = useState<
    DashboardRecentActivityApi[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [participatedOverride, setParticipatedOverride] = useState<
    number | null
  >(null); // authoritative user-specific participated count
  const [createdOverride, setCreatedOverride] = useState<number | null>(null); // authoritative user-specific created count
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const iconMap: Record<string, JSX.Element> = {
    bid: <Tag className="w-5 h-5 text-indigo-500" />,
    join: <UserPlus className="w-5 h-5 text-green-500" />,
    create: <PlusCircle className="w-5 h-5 text-blue-500" />,
    profile: <User className="w-5 h-5 text-yellow-500" />,
    default: <CheckCircle className="w-5 h-5 text-gray-500" />,
  };

  interface ActivityItem {
    type: string;
    action: string;
    time: string;
    icon?: JSX.Element;
  }

  // Check for welcome parameter (from login)
  useEffect(() => {
    if (searchParams.get('welcome') === 'true') {
      setShowWelcomeModal(true);
      // Remove the parameter from URL
      searchParams.delete('welcome');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Initialize data and load auctions
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Attempt remote fetch first
        const token = localStorage.getItem("authToken") || undefined; // supply auth token if present
        const remote = await dashboardService.fetchFullDashboard(token);
        if (!cancelled && remote.success) {
          setUseRemote(true);
          setWelcomeMessage(remote.dashboard.welcome_message);

          // @ts-ignore
          setRemoteStats(remote.dashboard.stats);
          setRemoteUpcoming(remote.dashboard.upcoming_auctions);
          setRemoteActivities(remote.dashboard.recent_activities);

          // Always compute authoritative user-specific counts (backend stats may be aggregate or incorrect per user)
          try {
            const userPhone = user?.phoneNumber;
            const userId = user?.id;


            // Fetch participated and created auctions in parallel instead of
            // one after the other -- they don't depend on each other, so
            // awaiting them sequentially was pure wasted latency (roughly
            // doubling this part of the dashboard's load time). Each keeps
            // its own try/catch so a failure in one doesn't affect the other,
            // exactly as before.
            const [participatedRaw, createdRaw] = await Promise.all([
              apiAuctionService
                .fetchFilteredAuctions({ status: "all", type: "participated" })
                .catch((participatedErr) => {
                  console.error(
                    "[DashboardHome] Failed to fetch participated auctions:",
                    participatedErr
                  );
                  return [] as BaseAuction[];
                }),
              apiAuctionService
                .fetchMyAuctions("all")
                .catch(() =>
                  apiAuctionService
                    .fetchFilteredAuctions({ status: "all", type: "created" })
                    .catch(() => {
                      console.error(
                        "[DashboardHome] Failed to fetch created auctions"
                      );
                      return [] as BaseAuction[];
                    })
                ),
            ]);

            // Filter participated auctions more accurately
            // If backend already filtered by type=participated, trust it but verify
            let participatedUser = participatedRaw;

            // If we got results, double-check they're actually for this user
            if (participatedRaw.length > 0 && userPhone) {
              participatedUser = participatedRaw.filter((a) => {
                // Check multiple ways a user might be associated with an auction
                const inParticipants = a.participants?.includes(userPhone);
                const inParticipantsList =
                  a.participantsList &&
                  Array.isArray(a.participantsList) &&
                  a.participantsList.some(
                    (p: any) =>
                      p?.phone_number === userPhone ||
                      p?.phoneNumber === userPhone
                  );

                // If backend returned it with type=participated, we can trust it
                // unless we can explicitly confirm they're NOT a participant
                return true; // Trust backend filtering when type=participated is specified
              });
            }

            // Filter created auctions more accurately
            const createdUser = (createdRaw || []).filter(
              (a) =>
                a.createdBy === userId ||
                a.userId === userId ||
                (a.created_by && a.created_by.toString() === userId) ||
                (a.creator_info && a.creator_info.id === userId)
            );

            if (!cancelled) {
              const participatedCount = participatedUser.length;
              const createdCount = createdUser.length;

              setParticipatedOverride(participatedCount);
              setCreatedOverride(createdCount);

            }
          } catch (recalcErr) {
            console.warn(
              "[DashboardHome] authoritative recount failed",
              recalcErr
            );
            // Use backend stats as fallback instead of zero
            if (!cancelled) {
              setParticipatedOverride(
                remote.dashboard.stats?.participated_auctions || 0
              );
              setCreatedOverride(remote.dashboard.stats?.total_created || 0);
            }
          }
        }
      } catch (e: any) {
        console.warn(
          "Remote dashboard fetch failed, falling back to local data",
          e
        );
        if (!cancelled) {
          setUseRemote(false);
          setError(
            e instanceof Error && e.message
              ? `${e.message}. Showing local data`
              : "Live dashboard unavailable, showing local data"
          );
          // Fallback local
          try {
            AuctionService.initializeData();
            const createdAuctions = AuctionService.getAuctionsByUser(user.id);
            setMyAuctions(createdAuctions);
            const participationAuctions =
              AuctionService.getAuctionsForParticipant(user.id);
            setParticipatedAuctions(participationAuctions);
          } catch (err) {
            console.error("Error loading local fallback dashboard data:", err);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Calculate dynamic stats
  const getStats = () => {

 if (useRemote && remoteStats) {
      const participatedCount =
        participatedOverride ?? remoteStats.participated_auctions;
      const createdCount = createdOverride ?? remoteStats.total_created;
      const CreateAuctionCount = remoteStats.active_auctions;

      return [
        {
          title: "My Active Auctions",
          value: remoteStats.total_created.toString(),
          change: `${CreateAuctionCount} total Live${
            createdOverride !== null ? " (verified)" : ""
          }`,
          icon: Gavel,
          color: "bg-blue-500",
        },
        {
          title: "Participated Auctions",
          value: remoteStats.participated_auctions.toString(),
          change: `${remoteStats.live_auctions} live now${
            participatedOverride !== null ? " (verified)" : ""
          }`,
          icon: Users,
          color: "bg-green-500",
        },
        {
          title: "Total Bids Placed",
          value: remoteStats.total_bids.toString(),
          change: `${remoteStats.winning_bids} winning`,
          icon: TrendingUp,
          color: "bg-purple-500",
        },
      ];
    }
    // Local fallback
    const activeAuctions = myAuctions.filter(
      (a) => a.status === "live" || a.status === "upcoming"
    );
    const liveAuctions = myAuctions.filter((a) => a.status === "live");
    const totalParticipated = participatedAuctions.length;
    const liveParticipated = participatedAuctions.filter(
      (a) => a.status === "live"
    );
    let totalBids = 0;
    participatedAuctions.forEach((auction) => {
      const bids = AuctionService.getBidsByAuction(auction.id);
      totalBids += bids.filter((bid) => bid.userId === user?.id).length;
    });

    return [
      {
        title: "My Active Auctions",
        value: myAuctions.length.toString(),
        change: `${liveAuctions.length} currently live`,
        icon: Calendar,
        color: "bg-blue-500",
      },
      {
        title: "Participated Auctions",
        value: totalParticipated.toString(),
        change: `${liveParticipated.length} live now`,
        icon: Users,
        color: "bg-green-500",
      },
      {
        title: "Total Bids Placed",
        value: totalBids.toString(),
        change: `across ${totalParticipated} auctions`,
        icon: TrendingUp,
        color: "bg-purple-500",
      },
    ];
  };

  // Get upcoming auctions (both created and participating)
  const getUpcomingAuctions = () => {
    if (useRemote) {
      return remoteUpcoming.slice(0, 5); // already prioritized by API
    }
    const allUpcoming: Array<
      BaseAuction & { role: "auctioneer" | "participant" }
    > = [];
    myAuctions
      .filter((a) => a.status === "upcoming" || a.status === "live")
      .slice(0, 2)
      .forEach((auction) => {
        allUpcoming.push({ ...auction, role: "auctioneer" });
      });
    participatedAuctions
      .filter((a) => a.status === "upcoming" || a.status === "live")
      .slice(0, 3 - allUpcoming.length)
      .forEach((auction) => {
        allUpcoming.push({ ...auction, role: "participant" });
      });
    return allUpcoming.slice(0, 3);
  };

  // Get recent activity
  const getRecentActivity = () => {
    if (useRemote) {
      return remoteActivities
        .map((a) => ({
          action: a.message,
          time: a.timestamp,
          type: a.type,
          // force icon mapping here instead of trusting backend
          icon: iconMap[a.type] || iconMap.default,
        }))
        .slice(0, 8);
    }

    const activities: Array<{
      action: string;
      time: string;
      type: string;
      icon?: string;
    }> = [];
    myAuctions.slice(0, 2).forEach((auction) => {
      activities.push({
        action: `Created auction "${auction.title}"`,
        time: "Recently",
        type: "create",
      });
    });
    participatedAuctions.slice(0, 2).forEach((auction) => {
      activities.push({
        action: `Joined auction "${auction.title}"`,
        time: "Recently",
        type: "join",
      });
    });
    return activities.slice(0, 4);
  };

  const stats = getStats();
  const upcomingAuctions = getUpcomingAuctions();
  const recentActivity = getRecentActivity();

  // Below, each data-dependent section renders its own skeleton while
  // `loading` is true instead of the page swapping out its whole layout for
  // a spinner -- the header, welcome card and quick actions never move, so
  // there's no jump when the real numbers arrive.

  return (
    <div className="ap-dashboard-home-wrapper">
      {/* Header Section */}
      <div className="ap-dashboard-home-header">
        <div className="ap-dashboard-home-header-content">
          <div className="ap-dashboard-home-title-section">
            <h1 className="ap-dashboard-home-title">
              <Activity className="w-8 h-8" />
              Dashboard Overview
            </h1>

            <p className="ap-dashboard-home-subtitle">
              Monitor your auction activities and performance
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="ap-dashboard-home-container">
        {/* Welcome Section */}
        <div className="ap-dashboard-home-welcome">
          <div className="ap-dashboard-home-welcome-header">
            <div className="ap-dashboard-home-welcome-text">
              <h2>Welcome back!</h2>
              <p>{welcomeMessage}</p>
              {error && (
                <p
                  style={{
                    color: "#f59e0b",
                    fontSize: "0.85rem",
                    marginTop: "4px",
                  }}
                >
                  {error}
                </p>
              )}
            </div>
            <Link
              to="/dashboard/new-auction"
              className="ap-dashboard-home-welcome-btn"
            >
              <Plus className="w-4 h-4" />
              Create Auction
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="ap-dashboard-home-stats">
          {loading
            ? Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="ap-dashboard-home-stat-card ap-skel-card">
                  <div className="ap-dashboard-home-stat-content">
                    <div className="ap-dashboard-home-stat-text">
                      <div className="ap-skel-line ap-skel-title" style={{ width: "70%" }} />
                      <div className="ap-skel-line" style={{ width: "40%", height: 22 }} />
                    </div>
                    <div className="ap-skel-badge" style={{ width: 48, height: 48, borderRadius: "0.75rem", flexShrink: 0 }} />
                  </div>
                </div>
              ))
            : stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="ap-dashboard-home-stat-card">
                    <div className="ap-dashboard-home-stat-content">
                      <div className="ap-dashboard-home-stat-text">
                        <h3>{stat.title}</h3>
                        <p className="ap-dashboard-home-stat-value">{stat.value}</p>
                      </div>
                      <div
                        className={`ap-dashboard-home-stat-icon ${
                          stat.color.includes("blue")
                            ? "blue"
                            : stat.color.includes("green")
                            ? "green"
                            : stat.color.includes("purple")
                            ? "purple"
                            : "orange"
                        }`}
                      >
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                );
              })}
        </div>

        <div className="ap-dashboard-home-content">
          {/* Upcoming Auctions */}
          <div className="ap-dashboard-home-section">
            <div className="ap-dashboard-home-section-header">
              <h2 className="ap-dashboard-home-section-title">
                Upcoming Auctions
              </h2>
              <Link
                to="/dashboard/MyCreatedA"
                className="ap-dashboard-home-section-link"
              >
                View all
              </Link>
            </div>
            <div className="ap-dashboard-home-section-body">
              {loading ? (
                <div className="ap-dashboard-home-auction-list">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} className="ap-dashboard-home-auction-item ap-skel-card">
                      <div className="ap-dashboard-home-auction-content">
                        <div className="ap-dashboard-home-auction-header">
                          <div className="ap-skel-line ap-skel-title" style={{ width: "55%" }} />
                          <div className="ap-skel-badge" />
                        </div>
                        <div className="ap-dashboard-home-auction-meta">
                          <div className="ap-skel-line" style={{ width: "45%" }} />
                          <div className="ap-skel-line" style={{ width: "35%" }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
              <>
              <div className="ap-dashboard-home-auction-list">
                {useRemote
                  ? upcomingAuctions.map((a: any) => (
                      <div
                        key={a.id}
                        className="ap-dashboard-home-auction-item"
                      >
                        <div className="ap-dashboard-home-auction-content">
                          <div className="ap-dashboard-home-auction-header">
                            <h3 className="ap-dashboard-home-auction-title">
                              {a.title}
                            </h3>
                            <span
                              className={`ap-dashboard-home-auction-badge ${
                                a.status.toLowerCase() === "live"
                                  ? "live"
                                  : "participant"
                              }`}
                            >
                              {" "}
                              {a.status.toUpperCase()}{" "}
                            </span>
                          </div>
                          <div className="ap-dashboard-home-auction-meta">
                            <span>
                              <Calendar />
                              {fmtDate(a.auction_date)} at{" "}
                              {fmtTime(`${a.auction_date} ${a.start_time}`)}
                            </span>
                            {/* Only the auctioneer sees how many are registered */}
                            {a.is_creator !== false && (
                              <span>
                                <Users />
                                {a.participant_count}{" "}
                                {a.participant_count === 1
                                  ? "participant"
                                  : "participants"}
                              </span>
                            )}
                          </div>
                        </div>
                        <Link
                          to={
                            a.is_creator === false
                              ? `/dashboard/auction/${a.id}`
                              : `/dashboard/my-auction/${a.id}`
                          }
                          className="ap-dashboard-home-auction-btn"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </Link>

                        {/*  my changes and make sure is not in live changes make it change */}
                      </div>
                    ))
                  : upcomingAuctions.map((auction: any) => (
                      <div
                        key={auction.id}
                        className="ap-dashboard-home-auction-item"
                      >
                        <div className="ap-dashboard-home-auction-content">
                          <div className="ap-dashboard-home-auction-header">
                            <h3 className="ap-dashboard-home-auction-title">
                              {auction.title}
                            </h3>
                            <span
                              className={`ap-dashboard-home-auction-badge ${
                                auction.status === "live"
                                  ? "live"
                                  : auction.role === "auctioneer"
                                  ? "auctioneer"
                                  : "participant"
                              }`}
                            >
                              {auction.status === "live"
                                ? "LIVE"
                                : auction.role}
                            </span>
                          </div>
                          <div className="ap-dashboard-home-auction-meta">
                            <span>
                              <Calendar />
                              {auction.auctionDate} at{" "}
                              {auction.auctionStartTime}
                            </span>
                            {auction.role === "auctioneer" &&
                              (() => {
                              const count =
                                AuctionService.getParticipantsByAuction(
                                  auction.id
                                ).length;
                              return (
                                <span>
                                  <Users />
                                  {count}{" "}
                                  {count === 1 ? "participant" : "participants"}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                        <Link
                          to={
                            auction.role === "auctioneer"
                              ? `/dashboard/my-auction/${auction.id}`
                              : `/dashboard/auction/${auction.id}`
                          }
                          className="ap-dashboard-home-auction-btn"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </Link>
                      </div>
                    ))}
              </div>
              {upcomingAuctions.length === 0 && (
                <div className="ap-dashboard-home-empty">
                  <Calendar className="ap-dashboard-home-empty-icon" />
                  <p className="ap-dashboard-home-empty-text">
                    No upcoming auctions
                  </p>
                  <Link
                    to="/dashboard/new-auction"
                    className="ap-dashboard-home-empty-btn"
                  >
                    Create your first auction
                  </Link>
                </div>
              )}
              </>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="ap-dashboard-home-section">
            <div className="ap-dashboard-home-section-header">
              <h2 className="ap-dashboard-home-section-title">
                Recent Activity
              </h2>
            </div>
            <div className="ap-dashboard-home-section-body">
              {loading ? (
                <div className="ap-dashboard-home-activity-list">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div
                      key={index}
                      className="ap-dashboard-home-activity-item ap-skel-card"
                    >
                      <div className="ap-skel-badge" style={{ width: 36, height: 36, borderRadius: "9999px" }} />
                      <div className="ap-dashboard-home-activity-content">
                        <div className="ap-skel-line" style={{ width: "70%" }} />
                        <div className="ap-skel-line short" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
              <div className="ap-dashboard-home-activity-list">
                {recentActivity.length === 0 && (
                  <div className="ap-dashboard-home-empty">
                    <Activity className="ap-dashboard-home-empty-icon" />
                    <p className="ap-dashboard-home-empty-text">
                      No recent activity yet
                    </p>
                  </div>
                )}
                {recentActivity.map((activity, index) => (
                  <div
                    key={index}
                    className="ap-dashboard-home-activity-item"
                  >
                    <div
                      className={`ap-dashboard-home-activity-icon ${
                        activity.type === "bid"
                          ? "bg-indigo-100"
                          : activity.type === "join"
                          ? "bg-green-100"
                          : activity.type === "create"
                          ? "bg-blue-100"
                          : activity.type === "profile"
                          ? "bg-yellow-100"
                          : "bg-gray-100"
                      }`}
                    >
                      {activity.icon}
                    </div>

                    <div className="ap-dashboard-home-activity-content">
                      <p className="ap-dashboard-home-activity-text">
                        {activity.action}
                      </p>
                      <p className="ap-dashboard-home-activity-time">
                        {activity.time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="ap-dashboard-home-actions">
          <div className="ap-dashboard-home-actions-header">
            <h2 className="ap-dashboard-home-actions-title">Quick Actions</h2>
          </div>
          <div className="ap-dashboard-home-actions-body">
            <div className="ap-dashboard-home-actions-grid">
              <Link
                to="/dashboard/new-auction"
                className="ap-dashboard-home-action-card"
              >
                <div className="ap-dashboard-home-action-content">
                  <span className="ap-dashboard-home-action-icon">
                    <Gavel />
                  </span>
                  <div className="ap-dashboard-home-action-text">
                    <h3>Create New Auction</h3>
                    <p>Set up a new auction quickly</p>
                  </div>
                </div>
              </Link>

              <Link
                to="/dashboard/MyCreatedA"
                className="ap-dashboard-home-action-card"
              >
                <div className="ap-dashboard-home-action-content">
                  <span className="ap-dashboard-home-action-icon">
                    <Search />
                  </span>
                  <div className="ap-dashboard-home-action-text">
                    <h3>Browse Auctions</h3>
                    <p>Find auctions to participate in</p>
                  </div>
                </div>
              </Link>

              <Link
                to="/dashboard/reports"
                className="ap-dashboard-home-action-card"
              >
                <div className="ap-dashboard-home-action-content">
                  <span className="ap-dashboard-home-action-icon">
                    <BarChart />
                  </span>
                  <div className="ap-dashboard-home-action-text">
                    <h3>View Reports</h3>
                    <p>Analyze auction performance</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Welcome Modal after login */}
      <SuccessModal
        isOpen={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
        title="Welcome Back!"
        message={`Welcome to your dashboard, ${user?.name || user?.personName || 'User'}! You have successfully logged in. Explore your auctions and start bidding!`}
      />
    </div>
  );
};

export default DashboardHome;

